import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Play, X, Dumbbell, ArrowLeft, Video, Loader2, Search } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { SEOHead } from '../hooks/usePageSEO';

const MEDIA = process.env.REACT_APP_BACKEND_URL;

const VideoGalleryPage = () => {
  const { language } = useLanguage();
  const en = language === 'en';
  const L = (fr, eng) => (en ? eng : fr);

  const [exercises, setExercises] = useState([]);
  const [muscleGroups, setMuscleGroups] = useState([]);
  const [activeGroup, setActiveGroup] = useState('');
  const [loading, setLoading] = useState(true);
  const [playing, setPlaying] = useState(null);
  const [query, setQuery] = useState('');

  useEffect(() => {
    setLoading(true);
    fetch(`${MEDIA}/api/exercises/public/videos`)
      .then((r) => r.json())
      .then((d) => {
        setExercises(d.exercises || []);
        setMuscleGroups(d.muscle_groups || []);
      })
      .finally(() => setLoading(false));
  }, []);

  const filtered = useMemo(() => {
    let list = activeGroup ? exercises.filter((e) => e.muscle_group === activeGroup) : exercises;
    const q = query.trim().toLowerCase();
    if (q) list = list.filter((e) => (en ? e.name_en : e.name_fr)?.toLowerCase().includes(q));
    return list;
  }, [exercises, activeGroup, query, en]);

  const groupsWithCount = useMemo(() => {
    const counts = {};
    exercises.forEach((e) => { counts[e.muscle_group] = (counts[e.muscle_group] || 0) + 1; });
    return muscleGroups.filter((g) => counts[g]).map((g) => ({ name: g, count: counts[g] }));
  }, [exercises, muscleGroups]);

  return (
    <>
      <SEOHead
        title={L('Galerie vidéos Sora 2 — Bibliothèque d\'exercices Body-Up', 'Sora 2 video gallery — Body-Up exercise library')}
        description={L('Découvrez en vidéo la bibliothèque de 449 exercices Body-Up, générée par IA (Sora 2) — chaque mouvement démontré avec une exécution parfaite.', 'Explore the 449-exercise Body-Up library in video — each movement demonstrated with perfect form, generated by AI (Sora 2).')}
        path="/videos-exercices"
      />
      <main className="min-h-screen bg-black" data-testid="video-gallery-page">
        <div className="pointer-events-none fixed inset-0 overflow-hidden -z-10" aria-hidden="true">
          <div className="absolute -top-32 right-0 w-[30rem] h-[30rem] bg-primary/12 rounded-full blur-[140px]" />
          <div className="absolute top-1/2 -left-32 w-[26rem] h-[26rem] bg-fuchsia-600/10 rounded-full blur-[140px]" />
        </div>

        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pt-28 pb-8">
          <Link to="/application" className="inline-flex items-center gap-2 text-sm text-zinc-400 hover:text-primary transition-colors mb-6" data-testid="gallery-back-link">
            <ArrowLeft className="w-4 h-4" /> {L("Retour à l'app Body-Up", 'Back to the Body-Up app')}
          </Link>
          <p className="text-primary text-xs font-bold uppercase tracking-[0.2em] mb-3 flex items-center gap-2">
            <Video className="w-3.5 h-3.5" /> {L('Bibliothèque vidéo Sora 2', 'Sora 2 video library')}
          </p>
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-white leading-tight mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
            {L('449 exercices, tous démontrés en vidéo', '449 exercises, all demonstrated on video')}
          </h1>
          <p className="text-zinc-400 text-base max-w-2xl" data-testid="gallery-video-count">
            {loading
              ? L('Chargement…', 'Loading…')
              : L(
                  `${exercises.length} vidéos déjà générées sur 449 — la bibliothèque se complète en continu.`,
                  `${exercises.length} videos already generated out of 449 — the library keeps growing.`
                )}
          </p>

          <div className="flex gap-2 overflow-x-auto pb-2 mt-8 -mx-4 px-4 sm:mx-0 sm:px-0" data-testid="gallery-filters">
            <div className="relative shrink-0 w-full sm:w-64 mr-1">
              <Search className="w-4 h-4 text-zinc-500 absolute left-3 top-1/2 -translate-y-1/2" />
              <input
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder={L('Rechercher un exercice…', 'Search an exercise…')}
                className="w-full pl-9 pr-3 py-2 rounded-full text-sm bg-white/[0.035] backdrop-blur-xl border border-white/10 text-white placeholder:text-zinc-500 focus:outline-none focus:border-primary/50"
                data-testid="gallery-search-input"
              />
            </div>
            <button
              onClick={() => setActiveGroup('')}
              className={`shrink-0 px-4 py-2 rounded-full text-sm font-bold border transition-colors ${!activeGroup ? 'bg-primary border-primary text-white shadow-[0_0_20px_-4px_rgba(217,70,239,0.7)]' : 'border-white/10 text-zinc-400 hover:border-primary/40 hover:text-white'}`}
              data-testid="gallery-filter-all"
            >
              {L('Tous', 'All')} ({exercises.length})
            </button>
            {groupsWithCount.map((g) => (
              <button
                key={g.name}
                onClick={() => setActiveGroup(g.name)}
                className={`shrink-0 px-4 py-2 rounded-full text-sm font-bold border transition-colors ${activeGroup === g.name ? 'bg-primary border-primary text-white shadow-[0_0_20px_-4px_rgba(217,70,239,0.7)]' : 'border-white/10 text-zinc-400 hover:border-primary/40 hover:text-white'}`}
                data-testid={`gallery-filter-${g.name}`}
              >
                {g.name} ({g.count})
              </button>
            ))}
          </div>
        </div>

        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pb-24">
          {loading ? (
            <div className="flex items-center justify-center py-24" data-testid="gallery-loading">
              <Loader2 className="w-8 h-8 text-primary animate-spin" />
            </div>
          ) : filtered.length === 0 ? (
            <p className="text-zinc-500 text-center py-24" data-testid="gallery-empty">{L('Aucune vidéo pour ce groupe pour le moment.', 'No video for this group yet.')}</p>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4" data-testid="gallery-grid">
              {filtered.map((ex) => (
                <button
                  key={ex.id}
                  onClick={() => setPlaying(ex)}
                  className="group relative rounded-2xl overflow-hidden border border-white/10 bg-zinc-900 aspect-[3/4] text-left shadow-[0_10px_30px_rgba(0,0,0,0.5)] hover:border-primary/50 transition-colors"
                  data-testid={`gallery-card-${ex.id}`}
                >
                  <img src={`${MEDIA}${ex.image_url}?w=320`} alt={en ? ex.name_en : ex.name_fr} loading="lazy" className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-110" />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/10 to-transparent" />
                  <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                    <span className="w-12 h-12 rounded-full bg-primary/90 flex items-center justify-center shadow-[0_0_25px_rgba(217,70,239,0.7)]">
                      <Play className="w-5 h-5 text-white fill-white" />
                    </span>
                  </div>
                  <div className="absolute bottom-0 left-0 right-0 p-3">
                    <span className="inline-block px-2 py-0.5 rounded-full text-[10px] font-bold bg-primary/20 text-primary border border-primary/30 mb-1.5">{ex.muscle_group}</span>
                    <p className="text-white text-sm font-semibold leading-snug line-clamp-2">{en ? ex.name_en : ex.name_fr}</p>
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>

        <AnimatePresence>
          {playing && (
            <motion.div
              initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
              className="fixed inset-0 z-[200] bg-black/90 backdrop-blur-sm flex items-center justify-center p-4"
              onClick={() => setPlaying(null)}
              data-testid="gallery-video-modal"
            >
              <motion.div
                initial={{ scale: 0.92, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.92, opacity: 0 }}
                onClick={(e) => e.stopPropagation()}
                className="relative max-w-sm w-full rounded-2xl overflow-hidden border border-primary/40 shadow-[0_0_60px_rgba(217,70,239,0.35)] bg-black"
              >
                <button
                  onClick={() => setPlaying(null)}
                  className="absolute top-3 right-3 z-10 w-9 h-9 rounded-full bg-black/60 flex items-center justify-center text-white hover:bg-black/80 transition-colors"
                  data-testid="gallery-video-modal-close"
                >
                  <X className="w-5 h-5" />
                </button>
                <video
                  src={`${MEDIA}${playing.video_url}`}
                  autoPlay loop controls playsInline
                  className="w-full aspect-[3/4] object-cover"
                  data-testid="gallery-video-modal-player"
                />
                <div className="p-4">
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold bg-primary/20 text-primary border border-primary/30 mb-1.5">
                    <Dumbbell className="w-2.5 h-2.5" /> {playing.muscle_group}
                  </span>
                  <p className="text-white font-bold">{en ? playing.name_en : playing.name_fr}</p>
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      </main>
    </>
  );
};

export default VideoGalleryPage;
